import {
  AutocompleteGenericEntity,
  FetchAutocomplete,
  FetchAutocompleteProps,
} from '@australiangreens/ag-internal-components';
import { useMemo, useState } from 'react';

import { FieldMetaData } from './types';

type BaseFetchAutocompleteByIdTFCProps<T extends AutocompleteGenericEntity> = Omit<
  FetchAutocompleteProps<T>,
  'value' | 'onChange'
>;

// Discriminated union, if idType isn't specified its the same as 'number'
// Could probably also use a generic like in DatePickerTFC
type FetchAutocompleteByIdTFCProps<T extends AutocompleteGenericEntity> =
  | (BaseFetchAutocompleteByIdTFCProps<T> & {
      onChange: (value: number[], fieldMetaData: FieldMetaData) => void;
      idType?: 'number';
      isEmptyValid?: boolean;
      initialValue?: T[];
    })
  | (BaseFetchAutocompleteByIdTFCProps<T> & {
      onChange: (value: string[], fieldMetaData: FieldMetaData) => void;
      idType: 'string';
      isEmptyValid?: boolean;
      initialValue?: T[];
    });

/** Covers all the simple filters fields that are an array of numbers or
 * strings.
 *
 * The idTypes prop (default='number') determines whether the value returned in
 * the onChange is converted to a number or a string.
 *
 * Its simply a wrapper around FetchAutocomplete to manage the state and pass
 *  back a value via onChange. This simplifies the parent.
 */
export default function FetchAutocompleteByIdTFC<T extends AutocompleteGenericEntity>({
  isEmptyValid = false,
  onChange,
  idType,
  initialValue,
  ...props
}: FetchAutocompleteByIdTFCProps<T>) {
  const [internalValue, setInternalValue] = useState<T[]>([]);
  const [touched, setTouched] = useState(false);

  const value = useMemo(() => {
    if (!touched && internalValue.length === 0 && initialValue?.length) {
      return initialValue;
    }
    return internalValue;
  }, [touched, internalValue, initialValue]);

  const handleOnChange = (newValue: T[]) => {
    setTouched(true);
    setInternalValue(newValue);

    const state = newValue.length > 0 ? 'valid' : isEmptyValid ? 'valid' : 'empty';

    if (idType === 'string') {
      onChange(
        newValue.map((x) => String(x.id)),
        { state, error: null }
      );
    } else {
      onChange(
        newValue.map((x) => Number(x.id)),
        { state, error: null }
      );
    }
  };

  return (
    <FetchAutocomplete
      onChange={handleOnChange}
      value={value}
      textFieldColor="secondary"
      {...props}
    />
  );
}
